import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { CalendarPlus, Trash, PencilSimple, Clock, User, MapPin } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = {
  user_id: '', event_id: '', date: '', start_time: '', end_time: '',
  triage_center: '', position: '', notes: ''
};

export function SchedulingTab() {
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [form, setForm] = useState({ ...INITIAL_FORM });

  // Filters
  const [filterEvent, setFilterEvent] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEvent && filterEvent !== 'all') params.append('event_id', filterEvent);
      if (filterUser && filterUser !== 'all') params.append('user_id', filterUser);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      const { data } = await axios.get(`${API_URL}/api/schedules?${params.toString()}`);
      setSchedules(data);
    } catch (error) {
      console.error('Failed to fetch schedules:', error.response?.status);
      toast.error('Failed to load schedules');
    } finally { setLoading(false); }
  }, [filterEvent, filterUser, filterDateFrom, filterDateTo]);

  const fetchUsersAndEvents = useCallback(async () => {
    try {
      const [usersRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/users`),
        axios.get(`${API_URL}/api/events`)
      ]);
      setUsers(usersRes.data);
      setEvents(eventsRes.data);
    } catch {
      console.error('Failed to load users/events for scheduling');
    }
  }, []);

  useEffect(() => { fetchUsersAndEvents(); }, [fetchUsersAndEvents]);
  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const resetForm = () => { setEditingSchedule(null); setForm({ ...INITIAL_FORM }); };

  const handleEdit = (s) => {
    setEditingSchedule(s);
    setForm({
      user_id: s.user_id, event_id: s.event_id, date: s.date,
      start_time: s.start_time || '', end_time: s.end_time || '',
      triage_center: s.triage_center || '', position: s.position || '', notes: s.notes || ''
    });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.event_id || !form.date) {
      toast.error('Employee, event, and date are required');
      return;
    }
    try {
      if (editingSchedule) {
        await axios.put(`${API_URL}/api/schedules/${editingSchedule.id}`, form);
        toast.success('Schedule updated');
      } else {
        await axios.post(`${API_URL}/api/schedules`, form);
        toast.success('Schedule created');
      }
      setShowDialog(false); resetForm(); fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/schedules/${id}`);
      toast.success('Schedule deleted'); fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  // Get triage centers for the selected event
  const selectedEvent = events.find(ev => ev.id === form.event_id);
  const triageCenters = selectedEvent?.triage_centers || [];

  // Group schedules by date for display
  const groupedByDate = schedules.reduce((acc, s) => {
    if (!acc[s.date]) acc[s.date] = [];
    acc[s.date].push(s);
    return acc;
  }, {});
  const sortedDates = Object.keys(groupedByDate).sort();

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <>
      <Card className="border-slate-200" data-testid="scheduling-tab">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle>Employee Scheduling</CardTitle>
            <CardDescription>{schedules.length} schedule entries</CardDescription>
          </div>
          <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-schedule-button">
            <CalendarPlus size={18} className="mr-2" /> Add Schedule
          </Button>
        </CardHeader>

        {/* Filters */}
        <div className="px-6 pb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="w-48">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Event</Label>
              <Select value={filterEvent} onValueChange={setFilterEvent}>
                <SelectTrigger className="rounded-sm h-9" data-testid="filter-event"><SelectValue placeholder="All Events" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {events.map(ev => <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">Employee</Label>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="rounded-sm h-9" data-testid="filter-user"><SelectValue placeholder="All Employees" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">From</Label>
              <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="rounded-sm h-9" data-testid="filter-date-from" />
            </div>
            <div className="w-40">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">To</Label>
              <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="rounded-sm h-9" data-testid="filter-date-to" />
            </div>
            {(filterEvent !== 'all' || filterUser !== 'all' || filterDateFrom || filterDateTo) && (
              <Button variant="ghost" size="sm" className="h-9 text-slate-500" onClick={() => { setFilterEvent('all'); setFilterUser('all'); setFilterDateFrom(''); setFilterDateTo(''); }} data-testid="clear-filters">
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-slate-500">Loading schedules...</p>
          ) : sortedDates.length === 0 ? (
            <div className="text-center py-12">
              <CalendarPlus size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No schedules found. Add a schedule to get started.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedDates.map(date => (
                <div key={date}>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 border-b border-slate-200 pb-1" data-testid={`schedule-date-${date}`}>
                    {formatDate(date)}
                  </h3>
                  <div className="space-y-2">
                    {groupedByDate[date].map(s => (
                      <div key={s.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-4 py-3" data-testid={`schedule-row-${s.id}`}>
                        <div className="flex items-center gap-6 flex-wrap">
                          <div className="flex items-center gap-2 min-w-[140px]">
                            <User size={16} className="text-slate-400" />
                            <span className="font-medium text-slate-900">{s.user_name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-2 min-w-[180px]">
                            <MapPin size={16} className="text-blue-500" />
                            <span className="text-slate-700 text-sm">{s.event_name}</span>
                          </div>
                          {(s.start_time || s.end_time) && (
                            <div className="flex items-center gap-1 text-sm text-slate-500">
                              <Clock size={14} />
                              <span>{s.start_time || '—'} - {s.end_time || '—'}</span>
                            </div>
                          )}
                          {s.triage_center && (
                            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{s.triage_center}</span>
                          )}
                          {s.position && (
                            <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">{s.position}</span>
                          )}
                          {s.notes && (
                            <span className="text-xs text-slate-400 italic max-w-[200px] truncate">{s.notes}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 ml-4">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(s)} data-testid={`edit-schedule-${s.id}`}><PencilSimple size={16} /></Button>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(s.id)} data-testid={`delete-schedule-${s.id}`}><Trash size={16} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSchedule ? 'Edit Schedule' : 'Add Schedule'}</DialogTitle>
            <DialogDescription>{editingSchedule ? 'Update the schedule entry' : 'Assign an employee to an event'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {/* Employee */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Employee *</Label>
              <Select value={form.user_id} onValueChange={v => setForm({ ...form, user_id: v })}>
                <SelectTrigger className="rounded-sm" data-testid="schedule-user-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} — {u.role === 'qa_qi' ? 'QA/QI' : u.role?.toUpperCase()} {u.certification ? `(${u.certification})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Event */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Event *</Label>
              <Select value={form.event_id} onValueChange={v => setForm({ ...form, event_id: v, triage_center: '' })}>
                <SelectTrigger className="rounded-sm" data-testid="schedule-event-select"><SelectValue placeholder="Select event" /></SelectTrigger>
                <SelectContent>
                  {events.map(ev => <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required className="rounded-sm" data-testid="schedule-date-input" />
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Start Time</Label>
                <Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} className="rounded-sm" data-testid="schedule-start-time" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">End Time</Label>
                <Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} className="rounded-sm" data-testid="schedule-end-time" />
              </div>
            </div>

            {/* Triage Center (if event has them) */}
            {triageCenters.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Triage Center</Label>
                <Select value={form.triage_center} onValueChange={v => setForm({ ...form, triage_center: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="schedule-triage-center"><SelectValue placeholder="Select center (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {triageCenters.map(tc => <SelectItem key={tc.id} value={tc.name}>{tc.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Position */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Position / Role</Label>
              <Input value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} placeholder="e.g., EMT, Paramedic, Supervisor" className="rounded-sm" data-testid="schedule-position-input" />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" className="rounded-sm" data-testid="schedule-notes-input" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-schedule-button">{editingSchedule ? 'Update' : 'Create Schedule'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
